import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { Etiqueta } from '@/features/tickets/badges';
import { formatarDataHora, formatarMinutos, mensagemDeErro } from '@/lib/format';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { chaves, lancarTempo, listarLancamentos, obterResumoDeTempo } from '@/features/tickets/tickets.api';
import type { Lancamento, TotalDeTempo } from '@/features/tickets/types';

/**
 * Tempo trabalhado.
 *
 * O total da subárvore aparece ao lado do próprio porque é ele que vai para a
 * fatura: o custo do caso é o do trabalho inteiro, e quem cobra não deveria
 * precisar somar cinco chamados à mão.
 */
export function WorklogPanel({ ticketId }: { ticketId: string }) {
  const clienteDeConsulta = useQueryClient();

  const [inicio, setInicio] = useState(() => agoraNoCampoLocal());
  const [minutos, setMinutos] = useState('30');
  const [faturavel, setFaturavel] = useState(true);
  const [nota, setNota] = useState('');
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const lancamentos = useQuery({
    queryKey: chaves.lancamentos(ticketId),
    queryFn: () => listarLancamentos(ticketId),
  });

  const resumo = useQuery({
    queryKey: chaves.tempo(ticketId),
    queryFn: () => obterResumoDeTempo(ticketId),
  });

  // Quem lançou cada tempo — mesma resolução em lote das outras listas.
  const pessoas = useDicionarioDePessoas(
    lancamentos.data?.map((lancamento) => lancamento.userId) ?? [],
  );

  const lancar = useMutation<Lancamento, Error, void>({
    mutationFn: () =>
      lancarTempo(ticketId, {
        startedAt: new Date(inicio).toISOString(),
        minutes: Number(minutos),
        billable: faturavel,
        ...(nota.trim() ? { note: nota.trim() } : {}),
      }),
    onSuccess: () => {
      setNota('');
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.lancamentos(ticketId) });
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.tempo(ticketId) });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErroLocal(null);

    const quantidade = Number(minutos);
    if (!Number.isInteger(quantidade) || quantidade < 1 || quantidade > 1440) {
      setErroLocal('Informe de 1 a 1440 minutos — o banco recusa mais de 24 h num lançamento.');
      return;
    }
    if (new Date(inicio).getTime() > Date.now() + 60_000) {
      setErroLocal('O início do trabalho não pode estar no futuro.');
      return;
    }
    lancar.mutate();
  }

  const erroDoLancamento = erroLocal ?? mensagemDeErro(lancar.error);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Totalizador titulo="Neste chamado" total={resumo.data?.own} />
        <Totalizador titulo="Com os subchamados" total={resumo.data?.subtree} destaque />
      </div>

      <form
        onSubmit={enviar}
        className="grid gap-3 rounded-md border border-border bg-card p-3 sm:grid-cols-[auto_auto_1fr_auto] sm:items-end"
      >
        <label className="space-y-1.5 text-sm">
          <span className="font-medium">Início</span>
          <input
            type="datetime-local"
            value={inicio}
            onChange={(evento) => setInicio(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </label>

        <label className="space-y-1.5 text-sm">
          <span className="font-medium">Minutos</span>
          <input
            type="number"
            min={1}
            max={1440}
            value={minutos}
            onChange={(evento) => setMinutos(evento.target.value)}
            className={`${CLASSE_DE_CAMPO} w-24 tabular-nums`}
          />
        </label>

        <label className="space-y-1.5 text-sm">
          <span className="font-medium">Nota</span>
          <input
            value={nota}
            onChange={(evento) => setNota(evento.target.value)}
            placeholder="O que foi feito"
            className={CLASSE_DE_CAMPO}
          />
        </label>

        <Button
          type="submit"
          size="sm"
          loading={lancar.isPending}
          icon={<Plus className="h-4 w-4" aria-hidden />}
        >
          Lançar
        </Button>

        <label className="flex items-center gap-2 text-sm sm:col-span-4">
          <input
            type="checkbox"
            checked={faturavel}
            onChange={(evento) => setFaturavel(evento.target.checked)}
            className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
          />
          Faturável
        </label>

        {erroDoLancamento ? (
          <p role="alert" className="text-sm text-destructive sm:col-span-4">
            {erroDoLancamento}
          </p>
        ) : null}
      </form>

      {lancamentos.isPending ? (
        <p className="text-sm text-muted-foreground">Carregando lançamentos…</p>
      ) : lancamentos.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDeErro(lancamentos.error)}
        </p>
      ) : lancamentos.data.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-5 py-10 text-center">
          <Clock className="h-5 w-5 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">Nenhum tempo lançado neste chamado.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
          {lancamentos.data.map((lancamento) => (
            <li
              key={lancamento.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-background px-4 py-2.5 text-sm"
            >
              <span className="w-36 flex-none font-medium tabular-nums">
                {formatarMinutos(lancamento.minutes)}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatarDataHora(lancamento.startedAt)}
              </span>
              {lancamento.billable ? (
                <Etiqueta className="bg-success/12 text-success ring-success/25">Faturável</Etiqueta>
              ) : (
                <Etiqueta className="bg-muted text-muted-foreground ring-border">Interno</Etiqueta>
              )}
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {lancamento.note ?? '—'}
              </span>
              <NomeDePessoa
                id={lancamento.userId}
                pessoas={pessoas}
                className="text-xs text-muted-foreground"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CLASSE_DE_CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors placeholder:text-muted-foreground ' +
  'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow';

function Totalizador({
  titulo,
  total,
  destaque = false,
}: {
  titulo: string;
  total: TotalDeTempo | undefined;
  destaque?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-4 py-3',
        destaque ? 'border-primary/30 bg-primary/5' : 'border-border bg-background',
      )}
    >
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{titulo}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">
        {total ? formatarMinutos(total.minutes) : '—'}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {total
          ? `${formatarMinutos(total.billableMinutes)} faturáveis · ${total.entries} ${
              total.entries === 1 ? 'lançamento' : 'lançamentos'
            }`
          : 'carregando…'}
      </p>
    </div>
  );
}

/**
 * Agora no formato que o `datetime-local` aceita.
 *
 * O campo trabalha em horário local e sem fuso; `toISOString()` devolveria UTC
 * e o operador veria o relógio três horas atrás do que está no pulso dele.
 */
function agoraNoCampoLocal(): string {
  const agora = new Date();
  const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
  return [
    agora.getFullYear(),
    '-',
    doisDigitos(agora.getMonth() + 1),
    '-',
    doisDigitos(agora.getDate()),
    'T',
    doisDigitos(agora.getHours()),
    ':',
    doisDigitos(agora.getMinutes()),
  ].join('');
}
